import React, { useCallback, useEffect, useMemo } from "react";
import "./SystemStatusFeedItem.scss";
import { CaptionBody, Colors, SmallHeading } from "src/components";
import { localize } from "src/l10n";
import SocialPostText from "src/social-feed/components/SocialPostText/SocialPostText";
import { SpintrTypes } from "src/typings";
import { UnstyledButton } from "src/ui";
import SystemStatusStartItem from "./SystemStatusStartItem";
import SystemStatusStatus from "./SystemStatusStatus";
import { formatDates } from "./utils";
import { InteractiveTextFormatOptions } from "src/utils/formatInteractiveText";
import CalypsoButton from "src/ui/components/Buttons/CalypsoButton/CalypsoButton";
import { useDispatch, useSelector } from "react-redux";
import { addLike, removeLike, setLikers } from "src/interactions/actions";

interface IProps {
    post: Spintr.ISocialSystemStatus;
    parentPost: Spintr.ISocialPostBase;
    interactiveTextOptions?: InteractiveTextFormatOptions | undefined;
    isMinified?: boolean;
    onExpand?: () => void;
}

const SystemStatusFeedItem = (props: IProps) => {
    const dispatch = useDispatch();
    const appStateData = useSelector<Spintr.AppState, any>((appState) => {
        return {
            interactions: appState.interactions[props.parentPost.Id],
            userId: appState.profile.active.id,
            userName: appState.profile.active.name,
        }
    });

    const isLiked = useMemo(() => (appStateData.interactions?.likers || []).some((liker: Spintr.IUser) => liker.id === appStateData.userId), [appStateData.interactions, appStateData.userId]);

    const toggleLike = useCallback(() => {
        if (isLiked) {
            dispatch(removeLike(props.parentPost.Id, { id: appStateData.userId, name: appStateData.userName }));
        } else {
            dispatch(addLike(props.parentPost.Id, { id: appStateData.userId, name: appStateData.userName }));
        }
    }, [dispatch, props.parentPost.Id, appStateData.userId, appStateData.userName, isLiked]);

    useEffect(() => {
        if (!appStateData.interactions) {
            dispatch(setLikers(props.parentPost.Id, props.parentPost.likers || []));
        }
    }, [dispatch, props.parentPost.Id, appStateData.interactions, props.parentPost.likers]);

    const uiData = useMemo(() => {
        return {
            title: props.post.isInitialStatus ?
                localize("Skapad") :
                props.post.systemStatusType === SpintrTypes.SystemStatusType.Ongoing ?
                    localize("Pagaende") :
                    props.post.systemStatusType === SpintrTypes.SystemStatusType.Planned ?
                        localize("Planerad") :
                        props.post.systemStatusType === SpintrTypes.SystemStatusType.Information ?
                            localize("Information") :
                            localize("Avklarad"),
            textColor: props.post.systemStatusType === SpintrTypes.SystemStatusType.Information ? "contentDark" : "white",
        }
    }, [props.post]);

    const className = useMemo(() => {
        return [
            "SystemStatusFeedItem",
            (props.isMinified ? " minified" : "")
        ].filter(x => x).join(" ");
    }, [props.isMinified]);

    const result = (
        <div className={className}>
            <SystemStatusStartItem
                url={"/operations/" + props.post.resourceId}
                icon="service"
                iconType="system-status"
                type={props.post.systemStatusType}
                renderMainContent={() => (
                    <div className="meta">
                        <div className="row responsive-row">
                            <SmallHeading color={uiData.textColor as Colors}>
                                {uiData.title}
                            </SmallHeading>
                            {props.isMinified && (
                                <div className="actions">
                                    <CalypsoButton icon="heart" title={localize("Gilla")} text={localize("Gilla")} theme="light" iconColor="light-blue" textColor="contentDark" iconType={isLiked ? "bold" : "outline"} onClick={toggleLike} />
                                    <CalypsoButton icon="message" title={localize("StallEnFraga")} text={localize("StallEnFraga")} theme="light" iconColor="light-blue" textColor="contentDark" onClick={props.onExpand} />
                                </div>
                            )}
                        </div>
                        <div className="row">
                            <SystemStatusStatus
                                text={formatDates(props.post.startDate, props.post.endDate)}
                                type={props.post.systemStatusType}
                                textColor={props.post.systemStatusType === SpintrTypes.SystemStatusType.Information || props.post.systemStatusType === SpintrTypes.SystemStatusType.Ongoing ? "contentDark" : "white"}
                            />
                        </div>
                        <div className="row text">
                            <SocialPostText
                                post={props.parentPost}
                                interactiveTextOptions={props.interactiveTextOptions}
                            />
                        </div>
                        {!!props.post.impact && (
                            <div className="row">
                                <CaptionBody color="gray">{localize("IMPACT") + ":"}</CaptionBody>
                                <CaptionBody color={uiData.textColor as Colors}>{localize("SYSTEM_STATUS_IMPACT_" + props.post.impact) + (props.post.impactDescription ? (" | " + props.post.impactDescription) : "")}</CaptionBody>
                            </div>
                        )}
                    </div>
                )}
            />
        </div>
    )

    if (props.isMinified) {
        return (
            <UnstyledButton className={className} onClick={props.onExpand}>
                {result}
            </UnstyledButton>
        )
    }

    return (
        <div className={className}>
            {result}
        </div>
    );
}

export default SystemStatusFeedItem;